'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';

interface ConceptTitleProps {
  title: string | null | undefined;
  sequenceNumber: number;
  onClick?: () => void;
}

export function ConceptTitle({
  title,
  sequenceNumber,
  onClick,
}: ConceptTitleProps) {
  const t = useTranslations('concept');

  const fullTitle = title
    ? t('conceptNumberWithTitle', { number: sequenceNumber, title })
    : t('conceptNumber', { number: sequenceNumber });
  const compactTitle = title
    ? `#${sequenceNumber} ${title}`
    : `#${sequenceNumber}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const measurerRef = useRef<HTMLSpanElement>(null);
  const [showFull, setShowFull] = useState(true);

  useEffect(() => {
    const container = containerRef.current;
    const measurer = measurerRef.current;
    if (!container || !measurer) return;

    const check = () => {
      setShowFull(measurer.offsetWidth <= container.clientWidth);
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(container);
    return () => observer.disconnect();
  }, [fullTitle]);

  const baseClasses = 'scroll-m-20 text-section-title text-center';
  const interactiveClasses = onClick
    ? 'cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-0 p-0'
    : '';

  const Tag = onClick ? 'button' : 'h2';
  const extraProps = onClick ? { type: 'button' as const, onClick } : {};

  return (
    <div ref={containerRef} className="relative w-full min-w-0">
      {/* Hidden measurer renders the full title to detect whether it fits. */}
      <span
        ref={measurerRef}
        aria-hidden
        className={cn(
          baseClasses,
          'invisible absolute left-0 top-0 whitespace-nowrap pointer-events-none',
        )}
      >
        {fullTitle}
      </span>
      <Tag
        {...extraProps}
        className={cn(
          baseClasses,
          'block w-full whitespace-nowrap overflow-hidden text-ellipsis',
          interactiveClasses,
        )}
      >
        {showFull ? fullTitle : compactTitle}
      </Tag>
    </div>
  );
}
